import React from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';

const RiskMonitorChart = ({ macroScore }) => {
  const { t } = useTranslation();
  
  if (!macroScore || !macroScore.pillar_scores) return null;

  // Define which pillars to monitor for risk (configurable)
  // H = MRS (Commodities), E/B/D = Key risk factors within MOS
  const RISK_PILLARS = ['H', 'E', 'B', 'D'];

  // Fallback translation keys for risk pillars (mapped from RISK_PILLARS)
  const PILLAR_TRANSLATION_KEYS = {
    'H': 'dashboard.riskMonitor.commodities',
    'E': 'dashboard.riskMonitor.fxStress',
    'B': 'dashboard.riskMonitor.inflation',
    'D': 'dashboard.riskMonitor.creditLiquidity',
  };

  // Dynamic mapping: use pillar_name from API if available, fallback to translation keys
  const getPillarDisplayName = (pillar) => {
    // Try to get from pillar_name first (from API)
    if (pillar.pillar_name) {
      return pillar.pillar_name.split('/')[0].trim(); // Take first part before "/"
    }
    
    // Fallback to translation keys
    return PILLAR_TRANSLATION_KEYS[pillar.pillar_code] 
      ? t(PILLAR_TRANSLATION_KEYS[pillar.pillar_code])
      : pillar.pillar_code;
  };

  // Color palette for risk pillars
  const RISK_COLORS = ['#ff6b6b', '#667eea', '#f093fb', '#764ba2'];

  // Prepare radar data dynamically from API
  const radarData = macroScore.pillar_scores
    .filter(p => RISK_PILLARS.includes(p.pillar_code))
    .map((pillar, index) => ({
      pillarCode: pillar.pillar_code,
      factor: getPillarDisplayName(pillar),
      score: parseFloat(pillar.pillar_score || pillar.raw_score || 0),
      fullMark: 100,
      color: RISK_COLORS[index % RISK_COLORS.length],
    }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 2,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            {data.factor}
          </Typography>
          <Typography variant="body2" sx={{ color: '#ff6b6b', fontWeight: 700 }}>
            {t('dashboard.riskMonitor.riskScore')}: {data.score.toFixed(1)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'background.paper',
          boxShadow: 'none',
        },
        transition: 'none',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #c92a2a 100%)',
                color: 'white',
                mr: 2,
              }}
            >
              <WarningIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('dashboard.riskMonitor.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.riskMonitor.subtitle')}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ 
            px: 2, 
            py: 1, 
            borderRadius: 1.5, 
            bgcolor: alpha('#ff6b6b', 0.1),
            textAlign: 'center',
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {t('dashboard.riskMonitor.mrsScore')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#ff6b6b' }}>
              {parseFloat(macroScore.mrs || 0).toFixed(1)}
            </Typography>
          </Box>
        </Box>

        {/* Radar Chart */}
        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={350} minHeight={350}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e0e0e0" />
              <PolarAngleAxis 
                dataKey="factor" 
                tick={{ fill: '#666', fontSize: 12 }}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fill: '#999', fontSize: 10 }}
              />
              <Radar
                name={t('dashboard.riskMonitor.riskScore')}
                dataKey="score"
                stroke="#ff6b6b"
                fill="#ff6b6b"
                fillOpacity={0.3}
                strokeWidth={2}
                activeDot={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} wrapperStyle={{ zIndex: 1000 }} animationDuration={50} animationEasing="ease-out" />
            </RadarChart>
          </ResponsiveContainer>
        </Box>

        {/* Risk Legend */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-around',
          alignItems: 'flex-end',
          mt: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          {radarData.map((item, idx) => {
            const isHighRisk = item.score > 60;
            return (
              <Box key={idx} sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {item.factor}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isHighRisk ? '#ff6b6b' : '#11998e', mb: 0.5 }}>
                  {item.score.toFixed(1)}
                </Typography>
                <Box sx={{ 
                  display: 'inline-block',
                  px: 1, 
                  py: 0.25, 
                  borderRadius: 1, 
                  bgcolor: isHighRisk ? alpha('#ff6b6b', 0.15) : alpha('#11998e', 0.15),
                  color: isHighRisk ? '#ff6b6b' : '#11998e',
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                    {isHighRisk ? t('dashboard.riskMonitor.highRisk') : t('dashboard.riskMonitor.lowRisk')}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RiskMonitorChart;

